import { resolvePosthogLandingConfig } from './communityConfig';
import {
	clearPosthogProject,
	getActivePosthogClient,
	getLatestSwitchOp,
	resetIfIdentified,
	switchPosthogProject,
} from './posthogClientManager';
import { registerTenantContext } from './tenantContextRegistrar';
import { isAnalyticsEnabled } from './env';
import {
	getTenantSnapshot,
	getUserSnapshot,
	mergeEventContextProperties,
	resetSpaceSnapshot,
	resetTenantSnapshot,
	resetUserSnapshot,
	setSpaceSnapshot,
	setTenantSnapshot,
	setUserSnapshot,
} from './analyticsContext';
import type {
	AnalyticsSpaceSnapshot,
	AnalyticsTenantSnapshot,
	AnalyticsUserContext,
	PosthogCommunityConfig,
	QueuedAnalyticsEvent,
} from './types';

const MAX_QUEUE = 100;

const eventQueue: QueuedAnalyticsEvent[] = [];
let lastInitializedApiKey: string | null = null;

function isDebug(): boolean {
	return false;
}

function dbg(...args: unknown[]): void {
	if (isDebug()) {
		// eslint-disable-next-line no-console
		console.debug('[analytics]', ...args);
	}
}

function readStoredDomain(): string {
	if (typeof window === 'undefined') {
		return '';
	}
	try {
		const d = localStorage.getItem('domain');
		if (d) {
			return d;
		}
	} catch {
		// ignore
	}
	return window.location.host || '';
}

function flushQueueForOp(expectedOp: number): void {
	const client = getActivePosthogClient();
	if (!client) {
		return;
	}
	const keep: QueuedAnalyticsEvent[] = [];
	for (const ev of eventQueue) {
		if (ev.opId !== expectedOp) {
			continue;
		}
		try {
			client.capture(ev.eventName, mergeEventContextProperties(ev.properties));
		} catch {
			// ignore single event failure
		}
	}
	for (const ev of eventQueue) {
		if (ev.opId !== expectedOp) {
			keep.push(ev);
		}
	}
	eventQueue.length = 0;
	eventQueue.push(...keep);
	dbg('flushed queue for op', expectedOp, 'remaining', eventQueue.length);
}

function enqueueEvent(eventName: string, properties: Record<string, unknown>): void {
	const opId = getLatestSwitchOp();
	if (eventQueue.length >= MAX_QUEUE) {
		eventQueue.shift();
		dbg('queue overflow; dropped oldest');
	}
	eventQueue.push({eventName, properties, opId});
	dbg('queued', eventName, 'op', opId);
}

/** Re-registers tenant/space/role context as PostHog super properties so it stays current. */
function refreshRegisteredContext(): void {
	const client = getActivePosthogClient();
	if (!client) {
		return;
	}
	registerTenantContext(client);
}

function applyIdentifyToActiveClient(): void {
	const client = getActivePosthogClient();
	if (!client) {
		return;
	}
	const tenantSnapshot = getTenantSnapshot();
	const userSnapshot = getUserSnapshot();
	const uid = userSnapshot.userId;
	try {
		const dom = tenantSnapshot.domain || readStoredDomain();
		if (!uid) {
			// Drop an identified user (real logout) and fall back to an anonymous distinct_id.
			// An already-anonymous visitor keeps their distinct_id: this runs on every
			// anonymous page init, and resetting there was splitting the www visit from the sign-up.
			resetIfIdentified(client);
			refreshRegisteredContext();
			if (dom) {
				client.register({domain: dom});
			}
			return;
		}
		const props: Record<string, unknown> = {userId: uid};
		if (userSnapshot.name != null && userSnapshot.name !== '') {
			props.name = userSnapshot.name;
		}
		if (userSnapshot.email != null && userSnapshot.email !== '') {
			props.email = userSnapshot.email;
		}
		if (dom) {
			props.domain = dom;
		}
		// Set role as PERSON properties (not only per-event props) so role can be a global
		// analytics dashboard filter across every tile, like geo/paid_user already are.
		if (userSnapshot.userRole != null && userSnapshot.userRole !== '') {
			props.userRole = userSnapshot.userRole;
		}
		if (userSnapshot.roleId != null && userSnapshot.roleId !== '') {
			props.roleId = userSnapshot.roleId;
		}
		// Firmographics, same reasoning as role above: PERSON properties so post analytics can
		// break viewers down by company / title / industry the way LinkedIn does. We already own
		// this data in the user profile; PostHog just has to be told.
		//
		// `industries` is an ARRAY - a viewer with several contributes 1/N to each on the server
		// (post-analytics-normalise.ts), which is what keeps a breakdown summing to 100%.
		if (userSnapshot.company != null && userSnapshot.company !== '') {
			props.company = userSnapshot.company;
		}
		if (userSnapshot.job_title != null && userSnapshot.job_title !== '') {
			props.job_title = userSnapshot.job_title;
		}
		if (Array.isArray(userSnapshot.industries) && userSnapshot.industries.length > 0) {
			props.industries = userSnapshot.industries;
		}
		// Event catalog: plan replaces the old paid_user flag; owner flags separate customers from members.
		if (userSnapshot.plan != null && userSnapshot.plan !== '') {
			props.plan = userSnapshot.plan;
		}
		if (typeof userSnapshot.is_community_owner === 'boolean') {
			props.is_community_owner = userSnapshot.is_community_owner;
		}
		if (typeof userSnapshot.communities_owned === 'number') {
			props.communities_owned = userSnapshot.communities_owned;
		}
		client.identify(uid, props);
	} catch {
		// never throw
	}
}

export async function initAnalytics(config: PosthogCommunityConfig): Promise<void> {
	if (typeof window === 'undefined') {
		return;
	}
	if (!isAnalyticsEnabled()) {
		dbg('init skipped: POSTHOG_ENABLED=false');
		lastInitializedApiKey = null;
		clearPosthogProject();
		return;
	}
	if (!config.apiKey?.trim()) {
		dbg('init skipped: empty api key');
		lastInitializedApiKey = null;
		clearPosthogProject();
		return;
	}
	const newKey = config.apiKey.trim();
	const client = getActivePosthogClient();
	if (lastInitializedApiKey === newKey && client) {
		setTenantSnapshot({
			communityId: config.communityId,
			communityName: config.communityName,
			domain: readStoredDomain(),
		});
		dbg('init same api key; tenant updated', config.communityId);
		refreshRegisteredContext();
		applyIdentifyToActiveClient();
		return;
	}
	setTenantSnapshot({
		communityId: config.communityId,
		communityName: config.communityName,
		domain: readStoredDomain(),
	});
	dbg('init project', config.communityId);
	const ok = await switchPosthogProject(config);
	if (!ok) {
		dbg('init aborted or failed');
		return;
	}
	lastInitializedApiKey = newKey;
	const op = getLatestSwitchOp();
	flushQueueForOp(op);
	applyIdentifyToActiveClient();
}

export function updateTenantSnapshot(partial: Partial<AnalyticsTenantSnapshot>): void {
	setTenantSnapshot({
		...partial,
		domain: partial.domain ?? (getTenantSnapshot().domain || readStoredDomain()),
	});
	refreshRegisteredContext();
	applyIdentifyToActiveClient();
}

/** Space = group; cleared when user leaves `/groups/:groupId` routes. */
export function updateSpaceSnapshot(partial: Partial<AnalyticsSpaceSnapshot>): void {
	setSpaceSnapshot(partial);
	refreshRegisteredContext();
}

export function identifyUser(user: AnalyticsUserContext | null | undefined): void {
	if (!user?.userId) {
		resetUserSnapshot();
		applyIdentifyToActiveClient();
		return;
	}
	setUserSnapshot({...getUserSnapshot(), ...user});
	applyIdentifyToActiveClient();
}

export function setUserProperty(key: string, value: string | number | boolean): void {
	const client = getActivePosthogClient();
	if (!client) {
		return;
	}
	try {
		client.setPersonProperties({[key]: value});
	} catch {
		// ignore
	}
}

export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
	if (typeof window === 'undefined') {
		return;
	}
	try {
		const merged = mergeEventContextProperties(properties ? {...properties} : {});
		const client = getActivePosthogClient();
		if (!client) {
			enqueueEvent(eventName, merged);
			return;
		}
		client.capture(eventName, merged);
	} catch {
		// never throw
	}
}

/**
 * Like trackEvent, but sent right away (beacon) for events fired just before a reset or a
 * redirect, e.g. auth_logout, which batching used to lose.
 */
export function trackEventNow(eventName: string, properties?: Record<string, unknown>): void {
	if (typeof window === 'undefined') {
		return;
	}
	try {
		const merged = mergeEventContextProperties(properties ? {...properties} : {});
		const client = getActivePosthogClient();
		if (!client) {
			enqueueEvent(eventName, merged);
			return;
		}
		client.capture(eventName, merged, {send_instantly: true});
	} catch {
		// never throw
	}
}

export function shutdownAnalyticsForTenant(): void {
	eventQueue.length = 0;
	lastInitializedApiKey = null;
	resetSpaceSnapshot();
	clearPosthogProject();
	dbg('shutdown analytics (no tenant key)');
}

export function resetAnalytics(): void {
	eventQueue.length = 0;
	resetUserSnapshot();
	resetSpaceSnapshot();
	resetTenantSnapshot();
	lastInitializedApiKey = null;
	clearPosthogProject();
	dbg('reset analytics');
}

/**
 * Logout: strip user context and queued events, keep a working PostHog client (default public
 * key when no per-tenant key) so client-side navigations still record anonymous events.
 */
export async function logoutSessionAnalytics(): Promise<void> {
	if (typeof window === 'undefined') {
		return;
	}
	eventQueue.length = 0;
	resetUserSnapshot();
	resetSpaceSnapshot();
	resetTenantSnapshot(readStoredDomain());
	const cfg = resolvePosthogLandingConfig({
		fallbackContext: {communityId: '', communityName: ''},
	});
	if (cfg) {
		await initAnalytics(cfg);
	} else {
		lastInitializedApiKey = null;
		clearPosthogProject();
		dbg('logout analytics: no public api key');
	}
}
